/**
 * AuthProvider - React context managing session tokens, refresh logic,
 * auth state, auto-refresh before expiry, LIFF auto-login, and browser OAuth.
 *
 * Requirements: 1.1, 1.2, 1.8, 2.1, 2.2, 25.1
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import apiClient, { registerTokenHandlers } from '@/services/api';
import { isLiff } from '@/services/environment';
import { initLiff, liffLogout } from '@/services/liff';
import { getActiveTosVersion } from '@/services/auth';

// --- Types ---

export interface AuthUser {
  userId: string;
  displayName: string;
  profileImageUrl?: string;
  lineVerified: boolean;
  role: 'user' | 'admin';
  accountStatus: string;
  acceptedTosVersion?: string;
}

interface AuthContextValue {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  activeTosVersion: string | null;
  login: (provider: 'liff' | 'line-oauth', payload: string) => Promise<void>;
  logout: () => Promise<void>;
  updateUser: (updates: Partial<AuthUser>) => void;
}

interface AuthResponse {
  sessionToken: string;
  expiresIn: number; // seconds
  user: AuthUser;
}

// --- Constants ---

const SESSION_EXPIRY_KEY = 'findoryhub_session_expiry';
const SESSION_TOKEN_KEY = 'findoryhub_session_token';
const SESSION_USER_KEY = 'findoryhub_session_user';
// Legacy key from the removed refresh-token flow. Cleared on logout/session
// teardown so stale values left by older app versions don't linger.
const LEGACY_REFRESH_TOKEN_KEY = 'findoryhub_refresh_token';

// --- Context ---

const AuthContext = createContext<AuthContextValue | null>(null);

// --- Provider ---

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTosVersion, setActiveTosVersion] = useState<string | null>(null);
  const sessionTokenRef = useRef<string | null>(null);

  // --- Session storage helpers ---
  //
  // The app uses long-lived (30-day) session tokens with sessionVersion-based
  // invalidation (checked server-side in GET /auth/session). There is no
  // refresh-token rotation: LIFF re-authenticates via the SDK on each app open
  // and the browser OAuth flow re-runs the code exchange, so an expired or
  // revoked token simply results in a clean logout.

  const persistSession = useCallback(
    (sessionToken: string, expiresAt: number, userData: AuthUser) => {
      sessionTokenRef.current = sessionToken;
      try {
        localStorage.setItem(SESSION_TOKEN_KEY, sessionToken);
        localStorage.setItem(SESSION_EXPIRY_KEY, String(expiresAt));
        localStorage.setItem(SESSION_USER_KEY, JSON.stringify(userData));
      } catch {
        // localStorage unavailable
      }
    },
    [],
  );

  const clearTokens = useCallback(() => {
    sessionTokenRef.current = null;
    try {
      localStorage.removeItem(SESSION_EXPIRY_KEY);
      localStorage.removeItem(SESSION_TOKEN_KEY);
      localStorage.removeItem(SESSION_USER_KEY);
      localStorage.removeItem(LEGACY_REFRESH_TOKEN_KEY);
    } catch {
      // localStorage unavailable
    }
  }, []);

  // --- Fetch session to get activeTosVersion ---

  const fetchSession = useCallback(async () => {
    try {
      const response = await apiClient.get<{
        user: {
          userId: string;
          displayName: string;
          profileImageUrl?: string;
          role: 'user' | 'admin';
          acceptedTosVersion?: string;
        };
        activeTosVersion: string;
      }>('/auth/session');
      const { user: sessionUser, activeTosVersion: version } = response.data;
      if (version) {
        setActiveTosVersion(version);
      }
      // Update user with acceptedTosVersion from session.
      // Never downgrade: if local state already has a version (e.g., just accepted),
      // don't overwrite with undefined from a stale session response.
      if (sessionUser?.acceptedTosVersion !== undefined) {
        setUser((prev) => {
          if (!prev) return prev;
          // Only update if the backend version is equal or newer
          if (prev.acceptedTosVersion && !sessionUser.acceptedTosVersion) return prev;
          return { ...prev, acceptedTosVersion: sessionUser.acceptedTosVersion };
        });
      }
    } catch (err) {
      // Session fetch failed — activeTosVersion remains null
      // This is non-fatal; TosGate will pass through when activeTosVersion is null
      if (process.env.NODE_ENV !== 'production') {
        console.warn('[AuthContext] fetchSession failed:', err);
      }
    }
  }, []);

  // --- Clear session (used by the API 401 interceptor and logout) ---
  // A 401 means the session token is expired or revoked. There is no refresh
  // step; we clear local auth state so the user is prompted to log in again.

  const clearSession = useCallback(async (): Promise<null> => {
    setUser(null);
    clearTokens();
    return null;
  }, [clearTokens]);

  // --- Login ---

  const login = useCallback(
    async (provider: 'liff' | 'line-oauth', payload: string) => {
      setIsLoading(true);
      try {
        let response: { data: AuthResponse };

        if (provider === 'liff') {
          // LIFF: exchange ID token
          response = await apiClient.post<AuthResponse>('/auth/line', {
            idToken: payload,
          });
        } else {
          // Browser LINE OAuth: exchange authorization code
          response = await apiClient.post<AuthResponse>('/auth/line/callback', {
            code: payload,
            redirectUri: `${window.location.origin}/auth/callback`,
          });
        }

        const { sessionToken, expiresIn, user: userData } = response.data;
        const expiresAt = Date.now() + expiresIn * 1000;

        // Both surfaces issue long-lived (30-day) session tokens with no refresh
        // token. Persist token + expiry + user so the API auth header and SPA
        // session survive in-app navigation; on expiry the user re-authenticates
        // (LIFF via the SDK on next open, browser via the OAuth code exchange).
        persistSession(sessionToken, expiresAt, userData);
        setUser(userData);
        // Fetch session to get activeTosVersion
        await fetchSession();
      } finally {
        setIsLoading(false);
      }
    },
    [persistSession, fetchSession],
  );

  // --- Logout ---

  const logout = useCallback(async () => {
    // No backend call: the session is a stateless JWT with no server-side
    // refresh-token record to revoke. (To force-invalidate sessions, bump the
    // user's sessionVersion server-side.) Clear local state and end the LIFF
    // session if applicable.
    setUser(null);
    setActiveTosVersion(null);
    clearTokens();

    // LIFF logout if in LIFF environment
    if (isLiff()) {
      liffLogout();
    }
  }, [clearTokens]);

  // --- Update user state (for ToS acceptance, profile updates, etc.) ---
  const updateUser = useCallback((updates: Partial<AuthUser>) => {
    setUser((prev) => {
      if (!prev) return prev;
      const updated = { ...prev, ...updates };
      // Persist to localStorage so the state survives page navigation
      try {
        localStorage.setItem(SESSION_USER_KEY, JSON.stringify(updated));
      } catch {
        // localStorage unavailable
      }
      return updated;
    });
  }, []);

  // --- Register token handlers with ApiClient ---
  // Register synchronously during render (not in useEffect) to avoid a race
  // where children's effects fire before AuthProvider's effect, causing
  // the first API call to be sent without an Authorization header.
  // Falls back to reading the token from localStorage when the ref isn't
  // populated yet (initial mount before initialize() has run).
  const handlersRegisteredRef = useRef(false);
  if (!handlersRegisteredRef.current) {
    handlersRegisteredRef.current = true;
    registerTokenHandlers({
      getToken: () => {
        if (sessionTokenRef.current) return sessionTokenRef.current;
        try {
          const stored = localStorage.getItem(SESSION_TOKEN_KEY);
          const expiry = localStorage.getItem(SESSION_EXPIRY_KEY);
          if (stored && expiry && Number(expiry) > Date.now()) {
            return stored;
          }
        } catch {
          // localStorage unavailable
        }
        return null;
      },
      refreshToken: () => clearSessionRef.current(),
    });
  }

  // Keep the latest clearSession accessible without re-registering handlers.
  const clearSessionRef = useRef<() => Promise<null>>(async () => null);
  useEffect(() => {
    clearSessionRef.current = clearSession;
  }, [clearSession]);

  // --- Initialize auth on mount ---

  useEffect(() => {
    let cancelled = false;

    async function initialize() {
      try {
        if (isLiff()) {
          // LIFF environment: auto-login via LIFF SDK (Requirement 25.1)
          const result = await initLiff();
          if (cancelled) return;

          if (result.isLoggedIn && result.idToken) {
            await login('liff', result.idToken);
            return;
          }
          // If not logged in, LIFF SDK will redirect to login
        } else {
          // Browser environment: check for OAuth callback
          const params = new URLSearchParams(window.location.search);
          const code = params.get('code');
          const state = params.get('state');

          if (code && state === 'line-oauth') {
            // Skip if we're on the /auth/callback page — let AuthCallbackPage handle it
            if (window.location.pathname === '/auth/callback') {
              // AuthCallbackPage will handle the code exchange
              setIsLoading(false);
              return;
            }
            // Handle LINE OAuth callback (Requirement 2.1, 2.2)
            // Clean URL params
            window.history.replaceState({}, '', window.location.pathname);
            await login('line-oauth', code);
            return;
          }

          // Try to restore session from stored session token (set by AuthCallbackPage)
          // This is the path used by /auth/line/callback which returns a sessionToken (no refresh token).
          try {
            const storedSessionToken = localStorage.getItem(SESSION_TOKEN_KEY);
            const storedExpiry = localStorage.getItem(SESSION_EXPIRY_KEY);
            const storedUserJson = localStorage.getItem(SESSION_USER_KEY);

            if (storedSessionToken && storedExpiry) {
              const expiresAt = Number(storedExpiry);
              if (Number.isFinite(expiresAt) && expiresAt > Date.now()) {
                sessionTokenRef.current = storedSessionToken;
                if (storedUserJson) {
                  try {
                    const restoredUser = JSON.parse(storedUserJson) as Partial<AuthUser>;
                    if (restoredUser && restoredUser.userId) {
                      setUser({
                        userId: restoredUser.userId,
                        displayName: restoredUser.displayName ?? 'User',
                        profileImageUrl: restoredUser.profileImageUrl,
                        lineVerified: restoredUser.lineVerified ?? true,
                        role: restoredUser.role ?? 'user',
                        accountStatus: restoredUser.accountStatus ?? 'active',
                        acceptedTosVersion: restoredUser.acceptedTosVersion,
                      });
                    }
                  } catch {
                    // user JSON corrupt — fall through
                  }
                }
                // Fetch session to get activeTosVersion
                await fetchSession();
                setIsLoading(false);
                return;
              }

              // Expired — clear stale session
              try {
                localStorage.removeItem(SESSION_TOKEN_KEY);
                localStorage.removeItem(SESSION_EXPIRY_KEY);
                localStorage.removeItem(SESSION_USER_KEY);
              } catch {
                // ignore
              }
            }
          } catch {
            // localStorage unavailable
          }

          // No stored session — user remains unauthenticated until they log in.
        }
      } catch {
        // Auth initialization failed — user remains unauthenticated
        if (!cancelled) {
          setUser(null);
          clearTokens();
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    initialize();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Context value ---

  // Reliably resolve the active ToS version from the PUBLIC /tos/active endpoint,
  // independent of /auth/session (which can fail silently). Runs once a user is
  // present and the version is still unknown, retrying a few times. Without this,
  // a failed session fetch leaves the gate comparing against the static fallback,
  // which would skip the re-acceptance prompt on a ToS version bump.
  useEffect(() => {
    if (!user || activeTosVersion) return;
    let cancelled = false;
    (async () => {
      for (let attempt = 0; attempt < 3 && !cancelled; attempt++) {
        const version = await getActiveTosVersion();
        if (cancelled) return;
        if (version) {
          setActiveTosVersion(version);
          return;
        }
        // Brief backoff before retrying a transient failure.
        await new Promise((resolve) => setTimeout(resolve, 500 * (attempt + 1)));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, activeTosVersion]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isAuthenticated: user !== null,
      isLoading,
      activeTosVersion,
      login,
      logout,
      updateUser,
    }),
    [user, isLoading, activeTosVersion, login, logout, updateUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// --- Hook ---

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// --- LINE OAuth redirect helper (for browser flow) ---

/**
 * Redirects the user to LINE OAuth authorization page.
 * Used in the browser environment for LINE login.
 */
export function redirectToLineOAuth(): void {
  const channelId = import.meta.env.VITE_LINE_CHANNEL_ID || '';
  const redirectUri = `${window.location.origin}/auth/callback`;
  const state = 'line-oauth';
  const scope = 'profile openid email';

  const url = new URL('https://access.line.me/oauth2/v2.1/authorize');
  url.searchParams.set('response_type', 'code');
  url.searchParams.set('client_id', channelId);
  url.searchParams.set('redirect_uri', redirectUri);
  url.searchParams.set('state', state);
  url.searchParams.set('scope', scope);

  window.location.href = url.toString();
}
